import { Autocomplete as MaterialAutocomplete, TextField, SxProps } from '@mui/material';
import React, { CSSProperties } from 'react';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import { bluePlanetTheme } from 'ui/theme';

interface Props<T> {
  id?: string;
  name?: string;
  placeholder?: string;
  options: T[];
  onChange: (value?: T) => void;
  onBlur?: () => void;
  onInputChange?: (newInput: string) => void;
  onClick?: () => void;
  value?: T;
  autoComplete?: string;
  autoFocus?: boolean;
  noOptionsText?: string;
  getOptionLabel?: (option: T) => string;
  renderOption?: (props: any, option: T) => React.ReactNode;
  helperText?: string;
  error?: boolean;
  style?: CSSProperties;
  isClearable?: boolean;
  className?: string;
  isDisabled?: boolean;
  color?: 'white' | 'transparent';
  isSuggestion?: boolean;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  inputRef?: React.Ref<HTMLDivElement>;
  sx?: SxProps;
  textFieldSx?: SxProps;
  getOptionKey?: (option: T) => string;
  onClear?: () => void;
}

const disableAutocompleteProps = {
  cursor: 'pointer',
  caretColor: 'transparent',
  '::selection': {
    color: bluePlanetTheme.bluePlanetPalette.indigo.main,
    backgroundColor: 'transparent',
  },
};
export default function Select<T>({
  value,
  name,
  id,
  placeholder,
  options,
  onChange,
  onBlur,
  onInputChange,
  onClick,
  noOptionsText,
  getOptionLabel,
  renderOption,
  error,
  helperText,
  autoComplete = 'not-active',
  autoFocus,
  style,
  className,
  isClearable = false,
  isDisabled = false,
  color = 'white',
  isOptionEqualToValue,
  sx,
  textFieldSx,
  inputRef,
  getOptionKey,
  onClear,
}: Props<T>) {
  // When there is no async search we don't need the caret
  const looksSearchable = !onInputChange;
  const colorProps =
    color === 'transparent'
      ? {
          backgroundColor: 'rgba(255,255,255,0.2)',
          color: bluePlanetTheme.bluePlanetPalette.white,
        }
      : {};

  const baseSx = Array.isArray(textFieldSx)
    ? Object.assign({}, ...textFieldSx)
    : typeof textFieldSx === 'object'
      ? textFieldSx
      : {};
  return (
    <MaterialAutocomplete
      value={value || null}
      id={id}
      style={style}
      disabled={isDisabled}
      getOptionKey={getOptionKey}
      popupIcon={<ChevronDownIcon color={color === 'transparent' ? 'white' : 'indigo'} fontSize="small" />}
      sx={{
        ...sx,
        '.MuiAutocomplete-inputFocused':
          color === 'transparent'
            ? {
                // make the input text white after a element has been selected
                color: bluePlanetTheme.bluePlanetPalette.white,
              }
            : {},
      }}
      renderInput={params => (
        <TextField
          inputRef={inputRef}
          variant="standard"
          {...params}
          name={name}
          onClick={onClick}
          error={error}
          placeholder={placeholder}
          className={className}
          sx={{
            ...baseSx,
            '.MuiInput-root .MuiInput-input::selection':
              color === 'transparent'
                ? {
                    color: `${bluePlanetTheme.bluePlanetPalette.white} !important`, // make the input text white when the dropdown is active
                  }
                : {},
            '.MuiInput-root': {
              ...baseSx['.MuiInput-root'],
              ...(looksSearchable ? disableAutocompleteProps : {}),
              ...colorProps,
            },
            '.MuiInput-root .MuiInput-input': {
              padding: 0,
              ...(looksSearchable ? disableAutocompleteProps : {}),
            },
          }}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          onBlur={onBlur}
          helperText={helperText}
        />
      )}
      noOptionsText={noOptionsText}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      onInputChange={
        onInputChange
          ? (_, newInputValue) => {
              onInputChange(newInputValue);
            }
          : undefined
      }
      onChange={(_, value) => (value ? onChange(value) : onClear?.())}
      options={options}
      disableClearable={!isClearable}
      openOnFocus={true}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}
